import i18next from "../../i18n";
import { isBackupWhiteRow } from "../systemColors";
// jsPDF + html2canvas (~551KB minified combined) are loaded on demand via
// dynamic import() inside the methods that need them, so they land in a
// separate chunk that is only fetched when a user actually downloads a PDF —
// keeping them out of the main bundle and off the first-load path.
import {
  BaseLogo,
  getLogoOrgId,
  isDigital,
  LogoType,
  NameLogo,
  NumberLogo,
} from "../../core/models/entities/BaseLogo";
import { Design } from "../../core/models/entities/Design";
import {
  getTransferDisplayName,
  getTypeText,
} from "../../core/models/entities/BaseLogo";
import {
  embroideryBackupRowLabel,
  isEmbroideryTransferType,
} from "../systemColors";
import { enviroment } from "../../enviroment";
import {
  findUnsharpLogoIndexes,
  isPdfSharpHandledStop,
  PdfNotSharpError,
  PdfSharpCancelledError,
  refetchEach,
  refetchFromBatch,
  RefetchUnsharp,
  toUnsharpLogoInfo,
  waitForSharpLogos,
} from "./pdfSharpGate";

export interface LogoPdfData {
  logo: BaseLogo;
  vendorLogoUrl?: string;
  // Optional (like DesignPdfData.apiClient). Only read under the flag
  // REACT_APP_PDF_WAIT_SHARP_ENABLED: lets the single-logo PDF re-fetch a
  // logo whose image is not sharp yet.
  apiClient?: any;
}

/**
 * Optional last argument of every public generate* method. Only read under
 * the flag REACT_APP_PDF_WAIT_SHARP_ENABLED - with the flag off it is ignored
 * and the methods behave exactly as before.
 */
export interface PdfSharpOptions {
  // true = never stop with PdfNotSharpError (the bounded wait still runs).
  allowUnsharp?: boolean;
  // Progress of the bounded sharp wait: `ready` of `total` image logos are
  // sharp. Only called when the PDF actually has to wait.
  onStatus?: (ready: number, total: number) => void;
  // For the callers WITHOUT an apiClient (the public proof screens): repeats
  // their ONE batch call and answers the fresh logos (logo PDFs) or designs
  // (design PDFs). Called at most twice, and only while a logo is unsharp.
  refetchAll?: () => Promise<BaseLogo[] | Design[]>;
  // The user's "Cancel" while the PDF waits for sharp logo images: the wait
  // ends at once (timer cleared), no further re-fetch is made and the method
  // rejects with PdfSharpCancelledError - nothing is built or saved.
  signal?: AbortSignal;
}

// Internal: PdfNotSharpError.downloadAnyway re-enters the same public method
// with the logos the stopped run had ALREADY fetched - no fetch, no wait, no
// gate, 0 new calls.
interface PdfSharpResumeOptions extends PdfSharpOptions {
  prefetchedLogos?: BaseLogo[];
}

export interface DesignPdfData {
  design: Design;
  vendorLogoUrl?: string;
  apiClient?: any;
}

export class LogoPdfGenerator {
  private static readonly PDF_WIDTH = 297; // A4 landscape width in mm
  private static readonly PDF_HEIGHT = 210; // A4 landscape height in mm
  private static readonly MARGIN = 5;
  private static readonly FONT_SIZE_TITLE = 16;
  private static readonly FONT_SIZE_LABEL = 10;
  private static readonly FONT_SIZE_VALUE = 10;
  private static readonly FONT_SIZE_LEGAL = 10;

  // One bounded retry for logos whose stored high-res blob was still being
  // rendered when the PDF fetched them (see HIGH_RES_RETRY_DELAY_MS below).
  private static readonly HIGH_RES_RETRY_DELAY_MS = 5000;

  // Cold hd_-cache marker (the frontend heal's #567 polling trigger, PDF
  // edition): on a stored-high-res MISS the API answers GetLogo with
  // HighResUrl null and enqueues a background render; the mapper's
  // `highResUrl: dto.highResUrl ?? lowResUrl` fallback then makes
  // highResUrl === lowResUrl (both version-stamped). Embedding that means a
  // ~180px thumbnail scaled to ~2000px on the logo page — grainy.
  private static isColdHighResMiss(logo: BaseLogo): boolean {
    const image = logo?.displayImage;
    return !!image?.highResUrl && image.highResUrl === image.lowResUrl;
  }

  // Waits once (bounded — never a loop) for the background render kicked off
  // by the first fetch, then re-fetches ONLY the cold-miss logos and swaps in
  // any answer that is now crisp. Every failure path keeps the first fetch's
  // image, so the PDF can never fail or hang because of this retry.
  private static async retryColdHighResLogos(
    logos: BaseLogo[],
    apiClient: any,
  ): Promise<void> {
    try {
      const pending = logos
        .map((logo, index) => ({ logo, index }))
        .filter(({ logo }) => this.isColdHighResMiss(logo));
      if (pending.length === 0) return;

      const { mapToLogo } = await import("../../data/models/mappers/mapLogo");

      await new Promise((resolve) =>
        setTimeout(resolve, this.HIGH_RES_RETRY_DELAY_MS),
      );

      const results = await Promise.allSettled(
        pending.map(({ logo }) =>
          apiClient.getLogo(getLogoOrgId(logo), logo.id).then(mapToLogo),
        ),
      );
      results.forEach((result, i) => {
        if (
          result.status === "fulfilled" &&
          !this.isColdHighResMiss(result.value)
        ) {
          logos[pending[i].index] = result.value;
        }
      });
    } catch (error) {
      console.warn(
        "High-res retry failed; keeping the first-fetch logo images:",
        error,
      );
    }
  }

  // ---- Flag REACT_APP_PDF_WAIT_SHARP_ENABLED (sharp gate) -----------------
  // Customer case 18/9-2026: a brand-new logo's stored 1000 px image did not
  // exist yet, the single 5 s retry above gave up, and the PDF silently
  // embedded a 60x68 px thumbnail scaled to ~2000 px. Under the flag the
  // retry above is replaced by pdfSharpGate's bounded wait (at most 2
  // re-fetches = 3 GetLogo per logo) and a PDF that would still contain an
  // unsharp image logo is STOPPED before anything is built or saved.

  private static prefetchedLogos(
    options: PdfSharpOptions | undefined,
  ): BaseLogo[] | undefined {
    return (options as PdfSharpResumeOptions | undefined)?.prefetchedLogos;
  }

  private static resumeOptions(logos: BaseLogo[]): PdfSharpOptions {
    const resume: PdfSharpResumeOptions = {
      allowUnsharp: true,
      prefetchedLogos: logos,
    };
    return resume;
  }

  // GetLogo with purpose=proof: on a stored-high-res miss the API queues the
  // render with reason "proof" (past the background gate) instead of behind
  // the price workers - the 81 s of the 18/9 case.
  private static proofLogoFetcher(
    apiClient: any,
    mapToLogo: (dto: any) => BaseLogo,
    orgIdOf: (logo: BaseLogo) => number = getLogoOrgId,
  ): (logo: BaseLogo) => Promise<BaseLogo> {
    return (logo) =>
      apiClient
        .getLogo(orgIdOf(logo), logo.id, { purpose: "proof" })
        .then(mapToLogo);
  }

  /**
   * Bounded wait (only when `refetch` is given) + the gate. Swaps sharp
   * answers into `logos` in place. Throws PdfNotSharpError when an image logo
   * is still not sharp and the caller did not pass allowUnsharp, and
   * PdfSharpCancelledError when options.signal was aborted; every other
   * failure keeps the images at hand (never fails the PDF by itself).
   */
  private static async ensureSharpLogos(
    logos: BaseLogo[],
    refetch: RefetchUnsharp | undefined,
    options: PdfSharpOptions | undefined,
    downloadAnyway: () => Promise<void>,
  ): Promise<void> {
    const measure = (url: string) => this.loadImageDimensions(url);
    const unsharp = refetch
      ? await waitForSharpLogos(
          logos,
          refetch,
          enviroment.pdfSharpWaitDelaysMs,
          options?.onStatus,
          { measure, signal: options?.signal },
        )
      : await findUnsharpLogoIndexes(logos, measure);
    // Cancelled by the user while it waited: never a PDF after a cancel,
    // whatever the last re-fetch answered.
    if (options?.signal?.aborted) throw new PdfSharpCancelledError();
    if (unsharp.length === 0 || options?.allowUnsharp) return;
    throw new PdfNotSharpError(
      unsharp.map((index) => toUnsharpLogoInfo(logos[index])),
      downloadAnyway,
    );
  }

  // Single-logo PDF (the logo popup). The popup's logo was fetched by an
  // EARLIER action, so a sharp one costs 0 calls exactly as today; only an
  // unsharp one pays the PDF's own GetLogo (purpose=proof - that call is what
  // queues the render past the gate) + at most 2 re-fetches = 3.
  private static async prepareSharpSingleLogo(
    data: LogoPdfData,
    options: PdfSharpOptions | undefined,
  ): Promise<BaseLogo> {
    const prefetched = this.prefetchedLogos(options);
    if (prefetched && prefetched[0]) return prefetched[0];

    const logos: BaseLogo[] = [data.logo];
    let refetch: RefetchUnsharp | undefined;
    if (data.apiClient) {
      try {
        const { mapToLogo } = await import("../../data/models/mappers/mapLogo");
        const getLogo = this.proofLogoFetcher(data.apiClient, mapToLogo);
        const measure = (url: string) => this.loadImageDimensions(url);
        if ((await findUnsharpLogoIndexes(logos, measure)).length > 0) {
          logos[0] = await getLogo(data.logo);
        }
        refetch = refetchEach(getLogo);
      } catch (error) {
        console.warn(
          "Sharp wait: first fetch failed; keeping the logo at hand:",
          error,
        );
      }
    }

    await this.ensureSharpLogos(logos, refetch, options, () =>
      this.generateLogoPdf(data, this.resumeOptions(logos)),
    );
    return logos[0];
  }

  // Logo list handed in by a caller without an apiClient (public proof
  // screens): gate only, plus the bounded wait when the caller can repeat
  // its batch call (options.refetchAll).
  private static async prepareSharpLogoList(
    logos: BaseLogo[],
    vendorLogoUrl: string | undefined,
    options: PdfSharpOptions | undefined,
  ): Promise<BaseLogo[]> {
    const prefetched = this.prefetchedLogos(options);
    if (prefetched) return prefetched;

    const prepared = [...logos];
    const refetchAll = options?.refetchAll;
    const refetch = refetchAll
      ? refetchFromBatch(async () => (await refetchAll()) as BaseLogo[])
      : undefined;
    await this.ensureSharpLogos(prepared, refetch, options, () =>
      this.generateMultipleLogosPdf(
        prepared,
        vendorLogoUrl,
        this.resumeOptions(prepared),
      ),
    );
    return prepared;
  }

  // Design PDFs: the unique logos are fetched, waited for and gated BEFORE
  // the first design page is rendered, so a stopped PDF costs no page work.
  private static async prepareSharpDesignLogos(
    designs: Design[],
    vendorLogoUrl: string | undefined,
    apiClient: any,
    options: PdfSharpOptions | undefined,
  ): Promise<BaseLogo[]> {
    const prefetched = this.prefetchedLogos(options);
    if (prefetched) return prefetched;

    const uniqueLogos = this.getUniqueLogosFromMultipleDesigns(designs);
    let logos: BaseLogo[] = [...uniqueLogos];
    let refetch: RefetchUnsharp | undefined;

    if (apiClient && uniqueLogos.length > 0) {
      try {
        const { mapToLogo } = await import("../../data/models/mappers/mapLogo");
        const getLogo = this.proofLogoFetcher(apiClient, mapToLogo);
        // Per-logo fallback, as in the classic path: one failed GetLogo must
        // not down-grade the rest (the gate below still judges the fallback).
        const results = await Promise.allSettled(
          uniqueLogos.map((logo) => getLogo(logo)),
        );
        logos = results.map((result, i) => {
          if (result.status === "fulfilled") return result.value;
          console.warn(
            `High-res fetch failed for logo id=${uniqueLogos[i].id} ` +
              `(orgId=${getLogoOrgId(uniqueLogos[i])}, title="${uniqueLogos[i].title}"):`,
            result.reason,
          );
          return uniqueLogos[i];
        });
        refetch = refetchEach(getLogo);
      } catch (error) {
        console.warn(
          "Failed to fetch high-res logo data, using existing logos:",
          error,
        );
      }
    } else if (options?.refetchAll) {
      const refetchAll = options.refetchAll;
      refetch = refetchFromBatch(async () =>
        this.getUniqueLogosFromMultipleDesigns(
          (await refetchAll()) as Design[],
        ),
      );
    }

    await this.ensureSharpLogos(logos, refetch, options, () =>
      this.generateMultipleDesignsPdf(
        designs,
        vendorLogoUrl,
        apiClient,
        this.resumeOptions(logos),
      ),
    );
    return logos;
  }

  /**
   * Generates and downloads a PDF proof for the given logo
   */
  public static async generateLogoPdf(
    data: LogoPdfData,
    options?: PdfSharpOptions,
  ): Promise<void> {
    try {
      // Lazy-load the heavy PDF libraries only when a download actually runs.
      const { default: jsPDF } = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");

      // Flag REACT_APP_PDF_WAIT_SHARP_ENABLED: bounded wait + gate BEFORE the
      // page is built (throws PdfNotSharpError instead of saving a blurred
      // logo). Flag off = `data` untouched, exactly today's flow.
      const pageData: LogoPdfData = enviroment.pdfWaitSharpEnabled
        ? { ...data, logo: await this.prepareSharpSingleLogo(data, options) }
        : data;

      // Create the HTML structure for the PDF
      const htmlElement = await this.createLogoPdfHtml(pageData);

      // Add to DOM temporarily for rendering (positioned off-screen)
      htmlElement.style.position = "absolute";
      htmlElement.style.top = "-9999px";
      htmlElement.style.left = "-9999px";
      htmlElement.style.zIndex = "-1000";
      document.body.appendChild(htmlElement);

      // Convert HTML to canvas
      const canvas = await html2canvas(htmlElement, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      // Remove from DOM
      document.body.removeChild(htmlElement);

      // Create PDF
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      // Add canvas to PDF with margin
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const contentWidth = this.PDF_WIDTH - 2 * this.MARGIN;
      const contentHeight = this.PDF_HEIGHT - 2 * this.MARGIN;
      pdf.addImage(
        imgData,
        "JPEG",
        this.MARGIN,
        this.MARGIN,
        contentWidth,
        contentHeight,
        undefined,
        "FAST",
      );

      // Download the PDF
      const fileName = `${data.logo.title}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      // The sharp gate's stop (the caller opens a dialog) and the user's
      // cancel are handled outcomes, not generator failures.
      if (!isPdfSharpHandledStop(error)) {
        console.error("Error generating PDF:", error);
      }
      throw error;
    }
  }

  /**
   * Generates and downloads a PDF with multiple logos (one page per logo)
   */
  public static async generateMultipleLogosPdf(
    logos: BaseLogo[],
    vendorLogoUrl?: string,
    options?: PdfSharpOptions,
  ): Promise<void> {
    try {
      if (logos.length === 0) {
        throw new Error("No logos provided for PDF generation");
      }

      // Flag REACT_APP_PDF_WAIT_SHARP_ENABLED: gate (+ bounded wait when the
      // caller passed refetchAll) BEFORE the first page is built. Flag off =
      // `logos` untouched.
      if (enviroment.pdfWaitSharpEnabled) {
        logos = await this.prepareSharpLogoList(logos, vendorLogoUrl, options);
      }

      // Lazy-load the heavy PDF library only when a download actually runs.
      const { default: jsPDF } = await import("jspdf");

      // Create PDF
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const contentWidth = this.PDF_WIDTH - 2 * this.MARGIN;
      const contentHeight = this.PDF_HEIGHT - 2 * this.MARGIN;

      // Add first logo page
      const firstLogoData: LogoPdfData = {
        logo: logos[0],
        vendorLogoUrl: vendorLogoUrl,
      };
      const firstLogoHtml = await this.createLogoPdfHtml(firstLogoData);
      const firstLogoCanvas = await this.createCanvasFromHtml(firstLogoHtml);
      const firstLogoImgData = firstLogoCanvas.toDataURL("image/jpeg", 0.92);
      pdf.addImage(
        firstLogoImgData,
        "JPEG",
        this.MARGIN,
        this.MARGIN,
        contentWidth,
        contentHeight,
        undefined,
        "FAST",
      );

      // Add remaining logo pages
      for (let i = 1; i < logos.length; i++) {
        const logoData: LogoPdfData = {
          logo: logos[i],
          vendorLogoUrl: vendorLogoUrl,
        };

        const logoHtml = await this.createLogoPdfHtml(logoData);
        const logoCanvas = await this.createCanvasFromHtml(logoHtml);

        // Add new page
        pdf.addPage();

        // Add logo canvas to PDF
        const logoImgData = logoCanvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(
          logoImgData,
          "JPEG",
          this.MARGIN,
          this.MARGIN,
          contentWidth,
          contentHeight,
          undefined,
          "FAST",
        );
      }

      // Download the PDF
      const allSameTitle = logos.every((l) => l.title === logos[0].title);
      const fileName = allSameTitle
        ? `${logos[0].title}.pdf`
        : `Logos Proof - ${logos.length} logos.pdf`;
      pdf.save(fileName);
    } catch (error) {
      if (!isPdfSharpHandledStop(error)) {
        console.error("Error generating multiple logos PDF:", error);
      }
      throw error;
    }
  }

  /**
   * Generates and downloads a PDF with multiple logos by fetching high-res data (one page per logo)
   */
  public static async generateMultipleLogosWithHighResPdf(
    logoIds: { logoId: number; orgId: number }[],
    apiClient: any,
    vendorLogoUrl?: string,
    options?: PdfSharpOptions,
  ): Promise<void> {
    try {
      if (logoIds.length === 0) {
        throw new Error("No logo IDs provided for PDF generation");
      }

      // Lazy-load the heavy PDF library only when a download actually runs.
      const { default: jsPDF } = await import("jspdf");

      // Import mapToLogo function
      const { mapToLogo } = await import("../../data/models/mappers/mapLogo");

      // Flag REACT_APP_PDF_WAIT_SHARP_ENABLED. Off = exactly today's calls.
      const sharpOn = enviroment.pdfWaitSharpEnabled;
      // Only set when PdfNotSharpError.downloadAnyway re-enters: the logos
      // the stopped run already fetched (0 new GetLogo).
      const prefetched = sharpOn ? this.prefetchedLogos(options) : undefined;

      // Fetch high-res logo data for all logos
      const logos: BaseLogo[] = prefetched
        ? prefetched
        : await Promise.all(
            logoIds.map(({ logoId, orgId }) =>
              (sharpOn
                ? apiClient.getLogo(orgId, logoId, { purpose: "proof" })
                : apiClient.getLogo(orgId, logoId)
              ).then(mapToLogo),
            ),
          );

      if (logos.length === 0) {
        throw new Error("Failed to fetch logo data");
      }

      if (sharpOn) {
        // Bounded wait (at most 2 re-fetches per cold logo = 3 GetLogo) and
        // the gate, BEFORE the first page is built. Re-fetches go to the org
        // the first call used.
        if (!prefetched) {
          const orgIdByLogoId = new Map<number, number>(
            logoIds.map(({ logoId, orgId }) => [logoId, orgId]),
          );
          const getLogo = this.proofLogoFetcher(
            apiClient,
            mapToLogo,
            (logo) => orgIdByLogoId.get(logo.id) ?? getLogoOrgId(logo),
          );
          await this.ensureSharpLogos(logos, refetchEach(getLogo), options, () =>
            this.generateMultipleLogosWithHighResPdf(
              logoIds,
              apiClient,
              vendorLogoUrl,
              this.resumeOptions(logos),
            ),
          );
        }
      } else {
        // Cold hd_-cache heal: one bounded retry for logos whose crisp stored
        // blob was still rendering on the first fetch (never fails the PDF).
        await this.retryColdHighResLogos(logos, apiClient);
      }

      // Create PDF
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const contentWidth = this.PDF_WIDTH - 2 * this.MARGIN;
      const contentHeight = this.PDF_HEIGHT - 2 * this.MARGIN;

      // Add first logo page
      const firstLogoData: LogoPdfData = {
        logo: logos[0],
        vendorLogoUrl: vendorLogoUrl,
      };
      const firstLogoHtml = await this.createLogoPdfHtml(firstLogoData);
      const firstLogoCanvas = await this.createCanvasFromHtml(firstLogoHtml);
      const firstLogoImgData = firstLogoCanvas.toDataURL("image/jpeg", 0.92);
      pdf.addImage(
        firstLogoImgData,
        "JPEG",
        this.MARGIN,
        this.MARGIN,
        contentWidth,
        contentHeight,
        undefined,
        "FAST",
      );

      // Add remaining logo pages
      for (let i = 1; i < logos.length; i++) {
        const logoData: LogoPdfData = {
          logo: logos[i],
          vendorLogoUrl: vendorLogoUrl,
        };

        const logoHtml = await this.createLogoPdfHtml(logoData);
        const logoCanvas = await this.createCanvasFromHtml(logoHtml);

        // Add new page
        pdf.addPage();

        // Add logo canvas to PDF
        const logoImgData = logoCanvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(
          logoImgData,
          "JPEG",
          this.MARGIN,
          this.MARGIN,
          contentWidth,
          contentHeight,
          undefined,
          "FAST",
        );
      }

      // Download the PDF
      const allSameTitle = logos.every((l) => l.title === logos[0].title);
      const fileName = allSameTitle
        ? `${logos[0].title}.pdf`
        : `Logos Proof - ${logos.length} logos.pdf`;
      pdf.save(fileName);
    } catch (error) {
      if (!isPdfSharpHandledStop(error)) {
        console.error(
          "Error generating multiple logos PDF with high-res:",
          error,
        );
      }
      throw error;
    }
  }

  /**
   * Generates and downloads a PDF with multiple designs and their unique logos
   */
  public static async generateMultipleDesignsPdf(
    designs: Design[],
    vendorLogoUrl?: string,
    apiClient?: any,
    options?: PdfSharpOptions,
  ): Promise<void> {
    try {
      if (designs.length === 0) {
        throw new Error("No designs provided for PDF generation");
      }

      // Flag REACT_APP_PDF_WAIT_SHARP_ENABLED: the unique logos are fetched,
      // waited for and gated BEFORE the first design page is rendered (throws
      // PdfNotSharpError instead of saving a blurred logo page). Flag off =
      // undefined = the classic fetch further down runs untouched.
      const sharpLogos = enviroment.pdfWaitSharpEnabled
        ? await this.prepareSharpDesignLogos(
            designs,
            vendorLogoUrl,
            apiClient,
            options,
          )
        : undefined;

      // Lazy-load the heavy PDF library only when a download actually runs.
      const { default: jsPDF } = await import("jspdf");

      // Create PDF
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const contentWidth = this.PDF_WIDTH - 2 * this.MARGIN;
      const contentHeight = this.PDF_HEIGHT - 2 * this.MARGIN;

      // Add first design page
      const firstDesignData: DesignPdfData = {
        design: designs[0],
        vendorLogoUrl: vendorLogoUrl,
      };
      const firstDesignHtml = this.createDesignPdfHtml(firstDesignData);
      const firstDesignCanvas =
        await this.createCanvasFromHtml(firstDesignHtml);
      const firstDesignImgData = firstDesignCanvas.toDataURL(
        "image/jpeg",
        0.92,
      );
      pdf.addImage(
        firstDesignImgData,
        "JPEG",
        this.MARGIN,
        this.MARGIN,
        contentWidth,
        contentHeight,
        undefined,
        "FAST",
      );

      // Add remaining design pages
      for (let i = 1; i < designs.length; i++) {
        const designData: DesignPdfData = {
          design: designs[i],
          vendorLogoUrl: vendorLogoUrl,
        };

        const designHtml = this.createDesignPdfHtml(designData);
        const designCanvas = await this.createCanvasFromHtml(designHtml);

        // Add new page
        pdf.addPage();

        // Add design canvas to PDF
        const designImgData = designCanvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(
          designImgData,
          "JPEG",
          this.MARGIN,
          this.MARGIN,
          contentWidth,
          contentHeight,
          undefined,
          "FAST",
        );
      }

      // Collect all unique logos from all designs
      const uniqueLogos = this.getUniqueLogosFromMultipleDesigns(designs);

      // Fetch high-res logo data if apiClient is provided.
      // Per-logo fallback: a single failure shouldn't down-grade the rest.
      let highResLogos = uniqueLogos;
      if (sharpLogos) {
        // Sharp gate: already fetched, waited for and judged above.
        highResLogos = sharpLogos;
      } else if (apiClient && uniqueLogos.length > 0) {
        try {
          const { mapToLogo } =
            await import("../../data/models/mappers/mapLogo");

          const results = await Promise.allSettled(
            uniqueLogos.map((logo) =>
              apiClient.getLogo(getLogoOrgId(logo), logo.id).then(mapToLogo),
            ),
          );

          let failureCount = 0;
          highResLogos = results.map((result, i) => {
            if (result.status === "fulfilled") return result.value;
            failureCount++;
            console.warn(
              `High-res fetch failed for logo id=${uniqueLogos[i].id} ` +
                `(orgId=${getLogoOrgId(uniqueLogos[i])}, title="${uniqueLogos[i].title}"):`,
              result.reason,
            );
            return uniqueLogos[i];
          });

          if (failureCount > 0) {
            console.warn(
              `High-res fetch: ${failureCount}/${uniqueLogos.length} logos fell back to existing data.`,
            );
          }

          // Cold hd_-cache heal: one bounded retry for logos whose crisp
          // stored blob was still rendering on the first fetch (never fails
          // the PDF — any error keeps the first answer).
          await this.retryColdHighResLogos(highResLogos, apiClient);
        } catch (error) {
          console.warn(
            "Failed to fetch high-res logo data, using existing logos:",
            error,
          );
          // Fall back to existing logos if the import or setup fails entirely
        }
      }

      // Add logo pages after all design pages
      for (const logo of highResLogos) {
        const logoData: LogoPdfData = {
          logo: logo,
          vendorLogoUrl: vendorLogoUrl,
        };

        const logoHtml = await this.createLogoPdfHtml(logoData);
        const logoCanvas = await this.createCanvasFromHtml(logoHtml);

        // Add new page
        pdf.addPage();

        // Add logo canvas to PDF
        const logoImgData = logoCanvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(
          logoImgData,
          "JPEG",
          this.MARGIN,
          this.MARGIN,
          contentWidth,
          contentHeight,
          undefined,
          "FAST",
        );
      }

      // Download the PDF
      const allSameTitle = designs.every((d) => d.title === designs[0].title);
      const fileName = allSameTitle
        ? `${designs[0].title}.pdf`
        : `Designs Proof - ${designs.length} designs.pdf`;
      pdf.save(fileName);
    } catch (error) {
      if (!isPdfSharpHandledStop(error)) {
        console.error("Error generating multiple designs PDF:", error);
      }
      throw error;
    }
  }

  public static async generateDesignPdf(
    data: DesignPdfData,
    options?: PdfSharpOptions,
  ): Promise<void> {
    // Delegate to the multi-design path so single and bulk downloads share the
    // exact same data flow (notably: the high-res logo re-fetch behaviour).
    return this.generateMultipleDesignsPdf(
      [data.design],
      data.vendorLogoUrl,
      data.apiClient,
      options,
    );
  }

  /**
   * Creates the HTML structure for the design PDF layout
   */
  static createDesignPdfHtml(data: DesignPdfData): HTMLElement {
    const container = document.createElement("div");
    container.style.cssText = `
      width: ${this.PDF_WIDTH}mm;
      height: ${this.PDF_HEIGHT}mm;
      position: relative;
      background: white;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      overflow: hidden;
    `;

    // Add main design image first so it stays behind other elements
    const mainDesign = this.createMainDesignImage(data.design);
    container.appendChild(mainDesign);

    // Add title section
    const titleSection = this.createDesignTitleSection(data);
    container.appendChild(titleSection);

    // Add info section
    const infoSection = this.createDesignInfoSection(data.design);
    container.appendChild(infoSection);

    // Add vendor logo (top-right)
    if (data.vendorLogoUrl) {
      const vendorLogo = this.createVendorLogo(data.vendorLogoUrl);
      container.appendChild(vendorLogo);
    }

    // Add legal text
    const legalText = this.createLegalTextForDesign();
    container.appendChild(legalText);

    // Add logos on design section
    const logosSection = this.createLogosOnDesignSection(data.design);
    container.appendChild(logosSection);

    return container;
  }

  /**
   * Creates the HTML structure for the logo PDF layout
   */
  static async createLogoPdfHtml(data: LogoPdfData): Promise<HTMLElement> {
    const container = document.createElement("div");
    container.style.cssText = `
      width: ${this.PDF_WIDTH}mm;
      height: ${this.PDF_HEIGHT}mm;
      position: relative;
      background: white;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      overflow: hidden;
    `;

    // Add logo title and SKU (top-left)
    const titleSection = this.createTitleSection(data);
    container.appendChild(titleSection);

    // Add logo information (top-left, below title)
    const infoSection = this.createInfoSection(data.logo);
    container.appendChild(infoSection);

    // Add vendor logo (top-right)
    if (data.vendorLogoUrl) {
      const vendorLogo = this.createVendorLogo(data.vendorLogoUrl);
      container.appendChild(vendorLogo);
    }

    // Add main logo image (center)
    const mainLogo = await this.createMainLogoImage(data.logo);
    container.appendChild(mainLogo);

    // Add legal text (bottom-left)
    const legalText = this.createLegalText();
    container.appendChild(legalText);

    return container;
  }

  /**
   * Creates the title section with logo name and SKU
   */
  private static createTitleSection(data: LogoPdfData): HTMLElement {
    const titleDiv = document.createElement("div");
    titleDiv.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      font-size: ${this.FONT_SIZE_TITLE}px;
      color: #596675;
      line-height: 1;
    `;
    const sku = data.logo.productSku || data.logo.product?.sku;

    // Create bold title element
    const titleSpan = document.createElement("span");
    titleSpan.style.fontWeight = "bold";
    titleSpan.textContent = data.logo.title;

    titleDiv.appendChild(titleSpan);

    // Create regular SKU element only if SKU exists
    if (sku) {
      const skuSpan = document.createElement("span");
      skuSpan.style.fontWeight = "normal";
      skuSpan.textContent = ` (${sku})`;
      titleDiv.appendChild(skuSpan);
    }

    return titleDiv;
  }

  /**
   * Creates the title section for design PDFs
   */
  private static createDesignTitleSection(data: DesignPdfData): HTMLElement {
    const titleDiv = document.createElement("div");
    titleDiv.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      font-size: ${this.FONT_SIZE_TITLE}px;
      color: #596675;
      line-height: 1;
    `;
    const titleSpan = document.createElement("span");
    titleSpan.style.fontWeight = "bold";
    titleSpan.textContent = data.design.title;

    titleDiv.appendChild(titleSpan);

    const mul = data.design.productMul || data.design.product?.mul;
    if (mul) {
      const idSpan = document.createElement("span");
      idSpan.style.fontWeight = "normal";
      idSpan.textContent = ` (${mul})`;
      titleDiv.appendChild(idSpan);
    }

    return titleDiv;
  }

  /**
   * Creates the logo information section
   */
  private static createInfoSection(logo: BaseLogo): HTMLElement {
    const infoDiv = document.createElement("div");
    infoDiv.style.cssText = `
      position: absolute;
      top: 30px;
      left: 10px;
      font-size: ${this.FONT_SIZE_LABEL}px;
      color: #596675;
      line-height: 1.4;
    `;

    // Determine size format based on logo type
    const isNameOrNumberLogo = logo.type === "name" || logo.type === "number";
    const sizeValue = isNameOrNumberLogo
      ? `${logo.heightMm}mm (height)`
      : `${logo.widthMm}×${logo.heightMm} mm`;

    const infoItems: { label: string; value: string }[] = [];

    // Only add ITEM NO. if it exists
    const sku = logo.productSku || logo.product?.sku;
    if (sku) {
      infoItems.push({ label: "ITEM NO.", value: sku });
    }

    // Always add these fields
    infoItems.push(
      { label: "LOGO NAME", value: logo.title || "" },
      { label: "TYPE", value: getTypeText(logo) },
      ...(getTypeText(logo) !== "Embroidery" &&
      getTypeText(logo) !== "Screen Print"
        ? [
            {
              label: "METHOD",
              value: getTransferDisplayName(logo)
                .toLowerCase()
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" "),
            },
          ]
        : []),
      { label: "SIZE", value: sizeValue },
    );

    // Add FONT field for Name and Number logos
    const hasFont = (logo as NameLogo | NumberLogo).font;
    if (hasFont) {
      infoItems.push({ label: "FONT", value: hasFont });
    }

    // Add regular info items
    infoItems.forEach((item) => {
      const itemDiv = document.createElement("div");
      itemDiv.style.cssText = `
        margin-bottom: 8px;
        display: flex;
        flex-direction: column;
      `;

      const labelSpan = document.createElement("span");
      labelSpan.style.cssText = `
        font-weight: bold;
        font-size: ${this.FONT_SIZE_LABEL}px;
        color: #596675;
        margin-bottom: 2px;
      `;
      labelSpan.textContent = item.label;

      const valueSpan = document.createElement("span");
      valueSpan.style.cssText = `
        font-size: ${this.FONT_SIZE_VALUE}px;
        color: #596675;
      `;
      valueSpan.textContent = item.value;

      itemDiv.appendChild(labelSpan);
      itemDiv.appendChild(valueSpan);
      infoDiv.appendChild(itemDiv);
    });

    // Add colors section separately
    const colorsItemDiv = document.createElement("div");
    colorsItemDiv.style.cssText = `
      display: flex;
      flex-direction: column;
    `;

    const colorsLabelSpan = document.createElement("span");
    colorsLabelSpan.style.cssText = `
      font-weight: bold;
      font-size: ${this.FONT_SIZE_LABEL}px;
      color: #596675;
      margin-bottom: 8px;
    `;
    colorsLabelSpan.textContent = "COLORS";

    const colorsSection = this.createColorsSection(logo);

    colorsItemDiv.appendChild(colorsLabelSpan);
    colorsItemDiv.appendChild(colorsSection);
    infoDiv.appendChild(colorsItemDiv);

    return infoDiv;
  }

  /**
   * Creates the design information section
   */
  private static createDesignInfoSection(design: Design): HTMLElement {
    const infoDiv = document.createElement("div");
    infoDiv.style.cssText = `
      position: absolute;
      top: 30px;
      left: 10px;
      font-size: ${this.FONT_SIZE_LABEL}px;
      color: #596675;
      line-height: 1.4;
    `;

    const infoItems: { label: string; value: string }[] = [];

    // Only add ITEM NO. if it exists
    const itemNo = design.productMul || design.product?.mul;
    if (itemNo) {
      infoItems.push({ label: "ITEM NO.", value: itemNo });
    }

    const manufacturerValue =
      design.style?.manufacturer?.name || design.navManufacturerName || "";
    const modelValue = design.style?.model || design.navStyleName || "";

    infoItems.push(
      { label: "DESIGN NAME", value: design.title || "" },
      { label: "MANUFACTURER", value: manufacturerValue },
      { label: "MODEL", value: modelValue },
    );

    // Add regular info items
    infoItems.forEach((item) => {
      const itemDiv = document.createElement("div");
      itemDiv.style.cssText = `
        margin-bottom: 8px;
        display: flex;
        flex-direction: column;
      `;
      const labelSpan = document.createElement("span");
      labelSpan.style.cssText = `
        font-weight: bold;
        font-size: ${this.FONT_SIZE_LABEL}px;
        color: #596675;
        margin-bottom: 2px;
      `;
      labelSpan.textContent = item.label;

      const valueSpan = document.createElement("span");
      valueSpan.style.cssText = `
        font-size: ${this.FONT_SIZE_VALUE}px;
        color: #596675;
      `;
      valueSpan.textContent = item.value;

      itemDiv.appendChild(labelSpan);
      itemDiv.appendChild(valueSpan);
      infoDiv.appendChild(itemDiv);
    });

    return infoDiv;
  }

  /**
   * Creates the vendor logo element
   */
  private static createVendorLogo(vendorLogoUrl: string): HTMLElement {
    const vendorDiv = document.createElement("div");
    vendorDiv.style.cssText = `
      position: absolute;
      top: 0;
      right: 0;
      width: 40mm;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    const img = document.createElement("img");
    img.src = vendorLogoUrl;
    img.style.cssText = `
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    `;

    vendorDiv.appendChild(img);
    return vendorDiv;
  }

  // The display box the logo image fills on the page. The logo is scaled
  // (up or down) to fill this box while keeping its aspect ratio, so small
  // logos are shown large and legible. The SIZE field on the page states the
  // actual physical mm, and the footer notes the preview is not to scale.
  private static readonly LOGO_MAX_WIDTH_MM = 180;
  private static readonly LOGO_MAX_HEIGHT_MM = 120;

  /**
   * Creates the main logo image in the center, scaled to fill the display
   * box (LOGO_MAX_WIDTH_MM x LOGO_MAX_HEIGHT_MM) while preserving the
   * image's natural aspect ratio. The image is never stretched; the actual
   * physical size is communicated by the SIZE field, not the preview.
   */
  private static async createMainLogoImage(
    logo: BaseLogo,
  ): Promise<HTMLElement> {
    // Flag REACT_APP_PDF_WAIT_SHARP_ENABLED: a logo WITHOUT a displayImage
    // (awaiting-image / recovered - mapLogo allows it) is stopped by the
    // gate, and "Download anyway" must then still be able to finish the PDF:
    // its page gets an empty logo box instead of a TypeError here. Flag off
    // = the classic dereference, untouched.
    const imageUrl: string = enviroment.pdfWaitSharpEnabled
      ? logo.displayImage?.highResUrl || logo.displayImage?.digitUrl || ""
      : logo.displayImage.highResUrl || logo.displayImage.digitUrl;
    // Only ever true under the flag (flag off never gets here without a
    // displayImage - it throws above, as before).
    const noImage = enviroment.pdfWaitSharpEnabled && !imageUrl;
    const natural = noImage ? null : await this.loadImageDimensions(imageUrl);
    const size = this.computeLogoRenderSize(logo, natural);

    const logoDiv = document.createElement("div");
    logoDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${size.widthMm}mm;
      height: ${size.heightMm}mm;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Sharp gate only (see above): no URL = an empty logo box, no <img>.
    if (noImage) return logoDiv;

    const img = document.createElement("img");
    img.src = imageUrl;
    img.style.cssText = `
      width: ${size.widthMm}mm;
      height: ${size.heightMm}mm;
      object-fit: contain;
    `;
    img.alt = logo.title;

    logoDiv.appendChild(img);
    return logoDiv;
  }

  private static loadImageDimensions(
    url: string,
  ): Promise<{ naturalWidth: number; naturalHeight: number } | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () =>
        resolve({
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
        });
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  private static computeLogoRenderSize(
    logo: BaseLogo,
    natural: { naturalWidth: number; naturalHeight: number } | null,
  ): { widthMm: number; heightMm: number } {
    const maxW = this.LOGO_MAX_WIDTH_MM;
    const maxH = this.LOGO_MAX_HEIGHT_MM;

    if (!natural || !natural.naturalWidth || !natural.naturalHeight) {
      return { widthMm: maxW, heightMm: maxH };
    }

    const aspect = natural.naturalWidth / natural.naturalHeight;

    let widthMm: number;
    let heightMm: number;
    if (logo.logoType === LogoType.NameLogo) {
      heightMm = logo.heightMm;
      widthMm = heightMm * aspect;
    } else if (logo.logoType === LogoType.NumberLogo) {
      // The number image is two rows of digits (0-4 / 5-9), but heightMm
      // represents one row (the height of a single digit). Double it so the
      // rendered per-digit height matches the physical print size.
      heightMm = logo.heightMm;
      widthMm = heightMm * aspect;
    } else {
      widthMm = logo.widthMm;
      heightMm = widthMm / aspect;
    }

    if (!(widthMm > 0) || !(heightMm > 0)) {
      // Broken mm metadata: fall back to the box width, deriving the height
      // from the image's aspect ratio so the image is never stretched; the
      // box-fit scale below then clamps tall images to the box height.
      widthMm = maxW;
      heightMm = maxW / aspect;
    }

    // Fill the display box: scale both up and down (no 1-cap), keeping the
    // aspect ratio. fe109a1 turned the box into a pure ceiling, which made
    // small logos render at stamp size; the SIZE field and the "not to
    // scale" footer already convey the physical dimensions.
    const scale = Math.min(maxW / widthMm, maxH / heightMm);
    return {
      widthMm: widthMm * scale,
      heightMm: heightMm * scale,
    };
  }

  /**
   * Creates the main design image element
   */
  private static createMainDesignImage(design: Design): HTMLElement {
    const designDiv = document.createElement("div");
    designDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 280mm;
      height: 180mm;
      z-index: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Use originalUrl from displayImage (contains design with logos already applied)
    if (design.displayImage?.originalUrl) {
      const img = document.createElement("img");
      img.src = design.displayImage.originalUrl;
      img.style.cssText = `
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        border-radius: 4px;
      `;
      designDiv.appendChild(img);
    } else {
      // Fallback if no image is available
      const placeholder = document.createElement("div");
      placeholder.style.cssText = `
        width: 100mm;
        height: 100mm;
        background: #f0f0f0;
        border: 2px dashed #ccc;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #999;
        font-size: 12px;
      `;
      placeholder.textContent = "No Image";
      designDiv.appendChild(placeholder);
    }

    return designDiv;
  }

  /**
   * Creates logos on design section
   */
  private static createLogosOnDesignSection(design: Design): HTMLElement {
    const logosDiv = document.createElement("div");
    logosDiv.style.cssText = `
      position: absolute;
      bottom: 3mm;
      right: 3mm;
      font-size: ${this.FONT_SIZE_VALUE}px;
      color: #596675;
      line-height: 1.3;
      max-width: 45%;
    `;

    // Title
    const title = document.createElement("div");
    title.style.cssText = `
      font-weight: bold;
      font-size: ${this.FONT_SIZE_VALUE}px;
      color: #596675;
      margin-bottom: 1mm;
    `;
    title.textContent = "LOGOS ON DESIGN";
    logosDiv.appendChild(title);

    // Grid container for logos (4 rows, expanding columns)
    if (design.designLogos && design.designLogos.length > 0) {
      const gridContainer = document.createElement("div");
      gridContainer.style.cssText = `
        display: grid;
        grid-template-rows: repeat(4, 1fr);
        grid-auto-flow: column;
        gap: 1mm 3mm;
        font-size: ${this.FONT_SIZE_VALUE}px;
        color: #596675;
      `;

      design.designLogos.forEach((designLogo) => {
        const logoItem = document.createElement("div");
        logoItem.style.cssText = `
          font-size: ${this.FONT_SIZE_VALUE}px;
          color: #596675;
          white-space: nowrap;
        `;

        const itemNo =
          designLogo.logo.productSku || designLogo.logo.product?.sku || "";
        const method = getTransferDisplayName(designLogo.logo)
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        const height = Math.round(designLogo.heightMm);
        const width = Math.round(designLogo.widthMm);

        logoItem.textContent = `${itemNo} ${method} ${width}×${height} mm`;
        gridContainer.appendChild(logoItem);
      });

      logosDiv.appendChild(gridContainer);
    }

    return logosDiv;
  }

  /**
   * Creates the legal disclaimer text for logos
   */
  private static createLegalText(): HTMLElement {
    const legalDiv = document.createElement("div");
    legalDiv.style.cssText = `
      position: absolute;
      bottom: 3mm;
      left: 0;
      right: 0;
      font-size: ${this.FONT_SIZE_LEGAL}px;
      color: #596675;
      line-height: 1.3;
      max-width: 45%;
    `;

    const legalText = `Once you approve this proof, you confirm that you accept the logo, its size, the colours and the printing method.
Printed colours on the final product may differ from those you see on‑screen.
The illustration is provided solely to verify logo, colours and method; it is not to scale.`;

    legalDiv.textContent = legalText;
    return legalDiv;
  }

  /**
   * Creates the legal disclaimer text for design
   */
  private static createLegalTextForDesign(): HTMLElement {
    const legalDiv = document.createElement("div");
    legalDiv.style.cssText = `
      position: absolute;
      bottom: 3mm;
      left: 0;
      right: 0;
      font-size: ${this.FONT_SIZE_LEGAL}px;
      color: #596675;
      line-height: 1.3;
      max-width: 45%;
    `;

    const legalText = `Once you approve this proof, you confirm that you accept all logo positions shown, together with the specified sizes, colours and printing methods.
Printed colours on the final product may differ from those you see on‑screen.
The illustration is provided solely to verify logo positions, sizes, colours and methods; it is not to scale.`;

    legalDiv.textContent = legalText;
    return legalDiv;
  }

  /**
   * Creates color blocks for display
   */
  private static createColorsSection(logo: BaseLogo): HTMLElement {
    const colorsContainer = document.createElement("div");
    colorsContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 2.5mm;
    `;

    if (isDigital(logo)) {
      // Create digital color block
      const digitalBlock = this.createDigitalColorBlock();
      colorsContainer.appendChild(digitalBlock);
    } else if (logo.colorDetails && logo.colorDetails.length > 0) {
      // Backup-row labels are cut by the logo's METHOD (owner decision 31/8),
      // so the proof says the same as the screen — see createVectorColorBlock.
      const embroideryMethod = isEmbroideryTransferType(
        logo.transferMethod?.type,
      );
      // Create vector color blocks
      logo.colorDetails.forEach((color) => {
        const colorBlock = this.createVectorColorBlock(color, embroideryMethod);
        colorsContainer.appendChild(colorBlock);
      });
    } else if (logo.colors && logo.colors.length > 0) {
      // Fallback for simple color names
      logo.colors.forEach((colorName) => {
        const colorText = document.createElement("div");
        colorText.style.cssText = `
          font-size: ${this.FONT_SIZE_VALUE}px;
          color: #596675;
        `;
        colorText.textContent = colorName;
        colorsContainer.appendChild(colorText);
      });
    } else {
      // No colors available
      const noColors = document.createElement("div");
      noColors.style.cssText = `
        font-size: ${this.FONT_SIZE_VALUE}px;
        color: #596675;
      `;
      noColors.textContent = "N/A";
      colorsContainer.appendChild(noColors);
    }

    return colorsContainer;
  }

  /**
   * Creates a digital color block with conic gradient
   */
  private static createDigitalColorBlock(): HTMLElement {
    const container = document.createElement("div");
    container.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
    `;

    // html2canvas cannot render CSS conic-gradient, so bake the gradient onto
    // a canvas and embed it as an <img> instead.
    const colorBlock = document.createElement("img");
    colorBlock.src = this.createDigitalGradientDataUrl();
    colorBlock.style.cssText = `
      width: 12px;
      height: 12px;
      border-radius: 2px;
      transform: translateY(2mm);
    `;

    const label = document.createElement("span");
    label.style.cssText = `
      font-size: ${this.FONT_SIZE_VALUE}px;
      color: #596675;
    `;
    label.textContent = "Digital";

    container.appendChild(colorBlock);
    container.appendChild(label);
    return container;
  }

  /**
   * Renders the digital conic gradient to a PNG data URL.
   */
  private static createDigitalGradientDataUrl(): string {
    const size = 64;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    // CSS "from 180deg" starts at 6 o'clock; canvas angle 0 is at 3 o'clock,
    // so rotate by π/2 to align them.
    const gradient = ctx.createConicGradient(Math.PI / 2, size / 2, size / 2);

    const stops: [number, string][] = [
      [14.602730423212051 / 360, "#F00"],
      [58.12499821186066 / 360, "#F90"],
      [121.87499642372131 / 360, "#FAFF00"],
      [170.6250035762787 / 360, "#00FF38"],
      [230.625 / 360, "#00FFF0"],
      [288.7499928474426 / 360, "#0500FF"],
      [341.2500071525574 / 360, "#DB00FF"],
    ];

    for (const [offset, color] of stops) {
      gradient.addColorStop(offset, color);
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    return canvas.toDataURL("image/png");
  }

  /**
   * Creates a vector color block with color name
   */
  private static createVectorColorBlock(
    color: any,
    embroideryMethod = false,
  ): HTMLElement {
    const container = document.createElement("div");
    container.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
    `;

    // Backup LAYER rows (customer case 21/8): the stored RGB is a stand-in,
    // not the real colour — pale blue "PMS 0821 C" for white backup foil, a
    // 0/0/0 dummy for embroidery rows like "TONE I TONE" (serial 445790) — so
    // render a bordered white swatch. Identity-gated via isBackupWhiteRow on
    // print/transfer (bare-flag rows are "printed WITH white backup" catalog
    // properties and keep their real name/swatch — regression 21-25/8); on
    // EMBROIDERY every flagged row is a real backup row and shows its own
    // name verbatim (owner decision 31/8) — same rule as
    // systemColorDisplayName, so PDF and screen say the same. Plain inline
    // CSS only (html2canvas renders this block).
    const isBackup =
      !!color &&
      (isBackupWhiteRow(color) || (embroideryMethod && color.isBackup === true));

    const colorBlock = document.createElement("div");
    colorBlock.style.cssText = `
      width: 12px;
      height: 12px;
      border-radius: 2px;
      background: ${isBackup ? "#ffffff" : `rgb(${color.red}, ${color.green}, ${color.blue})`};
      ${isBackup ? "border: 1px solid #9aa4b1;" : ""}
      transform: translateY(2mm);
    `;

    const label = document.createElement("span");
    label.style.cssText = `
      font-size: ${this.FONT_SIZE_VALUE}px;
      color: #596675;
    `;
    label.textContent = isBackup
      ? embroideryMethod
        ? embroideryBackupRowLabel(color, (key) => i18next.t(key))
        : i18next.t("logoEditorComponents.backupWhite")
      : color.name;

    container.appendChild(colorBlock);
    container.appendChild(label);
    return container;
  }

  // Helper function to create canvas from HTML element
  private static async createCanvasFromHtml(
    htmlElement: HTMLElement,
  ): Promise<HTMLCanvasElement> {
    // Lazy-load the heavy rendering library only when a download actually runs.
    const { default: html2canvas } = await import("html2canvas");

    // Add to DOM temporarily for rendering (positioned off-screen)
    htmlElement.style.position = "absolute";
    htmlElement.style.top = "-9999px";
    htmlElement.style.left = "-9999px";
    htmlElement.style.zIndex = "-1000";
    document.body.appendChild(htmlElement);

    // Convert HTML to canvas
    const canvas = await html2canvas(htmlElement, {
      scale: 3,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
    });

    // Remove from DOM
    document.body.removeChild(htmlElement);

    return canvas;
  }

  // Helper function to get unique logos from multiple designs
  private static getUniqueLogosFromMultipleDesigns(
    designs: Design[],
  ): BaseLogo[] {
    const uniqueLogos: BaseLogo[] = [];
    const seenLogoIds = new Set<number>();

    for (const design of designs) {
      for (const designLogo of design.designLogos) {
        const logoId = designLogo.logoId;
        if (!seenLogoIds.has(logoId)) {
          seenLogoIds.add(logoId);
          uniqueLogos.push(designLogo.logo);
        }
      }
    }

    return uniqueLogos;
  }
}
